import React, { useMemo, useState } from 'react';
import {
  MdRefresh, MdGroups, MdReplay, MdBolt, MdTimer, MdWaterDrop, MdLightbulb,
  MdPrecisionManufacturing, MdManageSearch, MdExplore, MdStorefront, MdMenuBook,
  MdPhoneAndroid, MdLanguage, MdPhoneIphone,
} from 'react-icons/md';
import api from '../../ui/api';
import useData from '../useData';
import PageShell, { Button, Notice } from '../../ui/PageShell';
import Card, { StatCard, StatGrid, EmptyState } from '../../ui/Card';
import Table from '../../ui/Table';
import './rowad.css';

/**
 * «روّاد: حماس الطلاب» — what the pupils do in «روّاد», the coming
 * games page of the student app: which place they want first (their
 * sparks — 3 a day, so they choose), who comes back, how long they stay,
 * the water puzzle, their ideas and their devices.
 * Data: GET /api/v1/rowad/report (api/src/routes/rowad.js).
 */

export const ROWAD_PLACES = {
  workshop: { title: 'ورشة المخترع', building: 'الورشة', icon: MdPrecisionManufacturing },
  detective: { title: 'محقّق المعرفة', building: 'المكتبة', icon: MdManageSearch },
  explorer: { title: 'رحّالة الحضارات', building: 'المرصد', icon: MdExplore },
  market: { title: 'سوق المدينة', building: 'السوق', icon: MdStorefront },
  quran: { title: 'رحلة القرآن والسيرة', building: 'دار القرآن', icon: MdMenuBook },
  creator: { title: 'صانع التحديات', building: 'المختبر', icon: MdLightbulb },
};

const WHO = { none: 'الطالب', father: 'الأب', mother: 'الأم' };

const n = (v) => Number(v || 0).toLocaleString('en-US');
const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
const when = (iso) => (iso
  ? new Date(iso).toLocaleString('ar-SY', { dateStyle: 'medium', timeStyle: 'short' })
  : '—');
/** 185000 → «3 د 5 ث». */
function duration(ms) {
  if (ms == null) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} ث`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} د${s % 60 ? ` ${s % 60} ث` : ''}`;
  return `${Math.floor(m / 60)} س ${m % 60} د`;
}

function deviceText(d) {
  if (!d) return '—';
  if (d.platform === 'web') return d.os ? `متصفح (${d.os})` : 'متصفح';
  return [d.model, d.os].filter(Boolean).join(' · ') || d.platform || '—';
}
function DeviceIcon({ d }) {
  if (!d) return null;
  if (d.platform === 'web') return <MdLanguage />;
  if (String(d.platform).toLowerCase() === 'ios') return <MdPhoneIphone />;
  return <MdPhoneAndroid />;
}

/** One place: a bar sized by its sparks, the count at the bar's end. */
function PlaceBar({ p, max }) {
  const meta = ROWAD_PLACES[p.key] || { title: p.key, icon: MdBolt };
  const Icon = meta.icon;
  const width = max ? (p.sparks / max) * 100 : 0;
  return (
    <div className="rw-place" tabIndex={0}>
      <div className="rw-place-name">
        <Icon aria-hidden="true" />
        <div>
          <b>{meta.title}</b>
          <small>{meta.building}</small>
        </div>
      </div>
      <div className="rw-place-bar">
        <div className="rw-track">
          <span className="rw-fill" style={{ width: `${Math.max(width, p.sparks ? 2 : 0)}%` }} />
        </div>
        <span className="rw-place-value"><b>{n(p.sparks)}</b> شرارة</span>
      </div>
      <div className="rw-place-facts">
        من {n(p.pioneers)} {p.pioneers === 1 ? 'طالب' : 'طلاب'}
        {' · '}فُتحت تفاصيله {n(p.views)} مرة
        {p.avg_view_ms != null && <>{' · '}قراءة {duration(p.avg_view_ms)} في المتوسط</>}
        {p.taps > 0 && <>{' · '}لُمس مبناه {n(p.taps)} مرة</>}
      </div>
    </div>
  );
}

/** Every calendar day of the last 30, missing ones as zero. */
function fillDays(days, span = 30) {
  const by = new Map(days.map((d) => [d.date, d]));
  const out = [];
  for (let i = span - 1; i >= 0; i -= 1) {
    const date = new Date(Date.now() + 3 * 3600000 - i * 86400000).toISOString().slice(0, 10);
    out.push(by.get(date) || { date, visitors: 0, sparks: 0, opens: 0 });
  }
  return out;
}

/** Pupils in «روّاد» per day (one series), details on hover and in a table. */
function DayChart({ days: raw }) {
  const [hover, setHover] = useState(null);
  const days = fillDays(raw);
  const max = Math.max(1, ...days.map((d) => d.visitors));
  const h = hover != null ? days[hover] : null;
  return (
    <div>
      <div className="rw-bars" onMouseLeave={() => setHover(null)} role="img" aria-label="الطلاب في روّاد يومياً">
        {days.map((d, i) => (
          <button
            key={d.date}
            type="button"
            className={`rw-bar${hover === i ? ' is-hover' : ''}`}
            onMouseEnter={() => setHover(i)}
            onFocus={() => setHover(i)}
            aria-label={`${d.date}: ${d.visitors} طالب، ${d.sparks} شرارة`}
          >
            <span style={{ height: `${d.visitors ? Math.max(4, (d.visitors / max) * 100) : 0}%` }} />
          </button>
        ))}
      </div>
      <div className="rw-bars-foot">
        <span dir="ltr">{days[0].date}</span>
        <span className="rw-bars-tip">
          {h
            ? <><b dir="ltr">{h.date}</b> — {n(h.visitors)} طالب · {n(h.sparks)} شرارة · {n(h.opens)} زيارة</>
            : 'مرّر على العمود لتفاصيل اليوم'}
        </span>
        <span dir="ltr">{days[days.length - 1].date}</span>
      </div>
      <details className="rw-details">
        <summary>الأرقام يوماً بيوم</summary>
        <table className="rw-mini-table">
          <thead><tr><th>اليوم</th><th>طلاب</th><th>شرارات</th><th>زيارات</th></tr></thead>
          <tbody>
            {[...raw].reverse().map((d) => (
              <tr key={d.date}><td dir="ltr">{d.date}</td><td>{n(d.visitors)}</td><td>{n(d.sparks)}</td><td>{n(d.opens)}</td></tr>
            ))}
            {!raw.length && <tr><td colSpan={4}>—</td></tr>}
          </tbody>
        </table>
      </details>
    </div>
  );
}

export default function Rowad() {
  const { data, loading, error, reload } = useData(() => api.rowadReport(), []);
  const t = data?.totals;
  const topScore = Math.max(1, ...(data?.students || []).map((s) => s.score));
  const maxSparks = Math.max(0, ...(data?.places || []).map((p) => p.sparks));

  const rows = useMemo(() => (data?.students || []).map((s, i) => ({
    ...s,
    key: s.student_id ?? `u${i}`,
    who_text: s.who.map((w) => WHO[w] || w).join('، '),
    top_title: ROWAD_PLACES[s.top_place]?.title || '—',
    device_text: deviceText(s.device),
    dwell_min: Math.round(s.dwell_ms / 60000),
  })), [data]);

  const columns = [
    {
      key: 'name', label: 'الطالب', width: 2,
      render: (r) => (
        <div className="rw-who">
          <b>{r.name}</b>
          <small>{r.section_label || '—'}</small>
        </div>
      ),
    },
    {
      key: 'score', label: 'الحماس', width: 1.2,
      render: (r) => (
        <div className="rw-meter" title={`${n(r.score)}`}>
          <span style={{ width: `${Math.max(4, (r.score / topScore) * 100)}%` }} />
        </div>
      ),
    },
    { key: 'who_text', label: 'من الحساب', filterable: true },
    { key: 'sparks', label: 'شرارات', align: 'center' },
    { key: 'days_active', label: 'أيام الزيارة', align: 'center' },
    { key: 'dwell_ms', label: 'وقت البقاء', align: 'center', render: (r) => duration(r.dwell_ms) },
    { key: 'views', label: 'تفاصيل قرأها', align: 'center' },
    {
      key: 'puzzles', label: 'لغز الماء', align: 'center',
      render: (r) => (r.puzzles ? <span className="rw-yes">✓ {r.puzzles > 1 ? `${r.puzzles} مرات · ` : ''}{r.best_puzzle_s} ث</span> : '—'),
    },
    { key: 'ideas', label: 'أفكار', align: 'center' },
    { key: 'top_title', label: 'أكثر ما يريده', filterable: true },
    {
      key: 'device_text', label: 'الجهاز', width: 1.4,
      render: (r) => <span className="rw-device"><DeviceIcon d={r.device} /> <bdi dir="ltr">{r.device_text}</bdi></span>,
    },
    { key: 'last_at', label: 'آخر زيارة', render: (r) => when(r.last_at) },
  ];

  return (
    <PageShell
      title="روّاد: حماس الطلاب"
      description={data ? `آخر تحديث: ${when(data.generated_at)}` : 'ما يريده الطلاب أولاً في صفحة «روّاد» بتطبيق الطالب'}
      actions={<Button variant="secondary" onClick={reload} loading={loading}>{!loading && <MdRefresh size={16} />} تحديث</Button>}
    >
      {error && <Notice tone="danger" title="تعذّر التحميل">{error}</Notice>}
      {!data && loading && <Card><p className="rw-muted">جارٍ التحميل…</p></Card>}

      {data && t.pioneers === 0 && (
        <Card>
          <EmptyState
            icon="🏙️"
            title="لم يدخل أحد مدينة الروّاد بعد"
            message="تظهر هنا حماسة الطلاب وأولياء الأمور فور دخولهم صفحة «روّاد» في تطبيق الطالب."
          />
        </Card>
      )}

      {data && t.pioneers > 0 && (
        <>
          <StatGrid>
            <StatCard icon={<MdGroups />} tone="info" label="طلاب دخلوا المدينة" value={n(t.pioneers)} hint={`من ${n(t.logins)} حساب (طلاب وأهالٍ)`} />
            <StatCard icon={<MdReplay />} tone="success" label="عادوا في يوم آخر" value={n(t.returning)} hint={`${pct(t.returning, t.pioneers)}% من الطلاب`} />
            <StatCard icon={<MdBolt />} tone="warning" label="شرارات «أريدها أسرع!»" value={n(t.sparks)} hint={`${n(t.opens)} زيارة للصفحة`} />
            <StatCard icon={<MdTimer />} tone="neutral" label="متوسط مدة الزيارة" value={duration(t.avg_dwell_ms)} />
            <StatCard icon={<MdWaterDrop />} tone="info" label="حلّوا لغز الماء" value={n(t.solvers)} hint={`${n(t.puzzles)} مرة`} />
            <StatCard icon={<MdLightbulb />} tone="success" label="أفكار من الطلاب" value={n(t.ideas)} />
          </StatGrid>

          <Card title="ماذا يريد الطلاب أولاً؟">
            <div className="rw-places">
              {data.places.map((p) => <PlaceBar key={p.key} p={p} max={maxSparks} />)}
            </div>
          </Card>

          <Card title="الطلاب في المدينة يومياً (آخر 30 يوماً)">
            <DayChart days={data.days} />
          </Card>

          <Card title="أكثر الطلاب حماساً" padded={false}>
            <Table
              columns={columns}
              rows={rows}
              keyField="key"
              searchPlaceholder="ابحث باسم الطالب أو الشعبة…"
              searchableKeys={['name', 'section_label', 'top_title', 'device_text']}
              pageSize={20}
              exportName="rowad-students"
              emptyText="لا أحد بعد"
            />
          </Card>

          {data.sections.length > 0 && (
            <Card title="حسب الشعبة">
              <div className="rw-table-wrap">
                <table className="rw-mini-table rw-sections">
                  <thead><tr><th>الشعبة</th><th>دخلوا المدينة</th><th>من طلاب الشعبة</th><th>شرارات</th></tr></thead>
                  <tbody>
                    {data.sections.map((s) => (
                      <tr key={s.section_id}>
                        <td><b>{s.section_label}</b></td>
                        <td>{n(s.pioneers)}</td>
                        <td>
                          <div className="rw-share">
                            <div className="rw-meter"><span style={{ width: `${pct(s.pioneers, s.students)}%` }} /></div>
                            <span>{pct(s.pioneers, s.students)}% <small>({n(s.pioneers)} من {n(s.students)})</small></span>
                          </div>
                        </td>
                        <td>{n(s.sparks)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          <Card title={`أفكار الطلاب (${n(data.ideas.length)})`}>
            {data.ideas.length === 0 ? (
              <p className="rw-muted">لا أفكار بعد.</p>
            ) : (
              <ul className="rw-ideas">
                {data.ideas.map((i) => (
                  <li key={i.id}>
                    <p>«{i.text}»</p>
                    <small>
                      {i.student_name || i.user_name}
                      {i.parent_type && i.parent_type !== 'none' ? ` (${WHO[i.parent_type]})` : ''}
                      {i.section_label ? ` · ${i.section_label}` : ''} · {when(i.created_at)}
                    </small>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="الأجهزة">
            <div className="rw-table-wrap">
              <table className="rw-mini-table">
                <thead><tr><th>الجهاز</th><th>النظام</th><th>إصدار التطبيق</th><th>أجهزة</th><th>حسابات</th><th>آخر ظهور</th></tr></thead>
                <tbody>
                  {data.devices.map((d, i) => (
                    <tr key={i}>
                      <td><span className="rw-device"><DeviceIcon d={d} /> <bdi dir="ltr">{d.platform === 'web' ? 'متصفح' : (d.model || d.platform || '—')}</bdi></span></td>
                      <td dir="ltr">{d.os || '—'}</td>
                      <td dir="ltr">{d.app_version || '—'}</td>
                      <td>{n(d.devices)}</td>
                      <td>{n(d.users)}</td>
                      <td>{when(d.last_seen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </PageShell>
  );
}
